import React from 'react';

import validate from '@vlabs/shared/validators';
import { Control, GeoLocation, GeoLocationMarker } from '@vlabs/uikit';
import { useDynamicCoordinates } from '@vlabs/uikit/components/geo-location/useDynamicCoordinates';
import PropTypes from 'prop-types';
import { useFormContext, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './GeopositionSubForm.module.sass';

export function GeopositionSubForm({ fieldName = undefined, closeModal = undefined }) {
  const { t } = useTranslation();

  const { setValue, getValues } = useFormContext();

  const createFullName = (name) => (fieldName ? `${fieldName}.${name}` : name);

  const { register, watch, handleSubmit, formState: { errors } } = useForm({ defaultValues: getValues(createFullName('location')), mode: 'onChange' });

  const onSubmit = handleSubmit((data) => {
    setValue(createFullName('location'), data);
    closeModal();
  });

  const longitude = watch('geo_position.longitude');
  const latitude = watch('geo_position.latitude');

  const position = useDynamicCoordinates({ latitude, longitude });

  return (
    <form className={st.Form}>
      <h1 className={st.Title}>{t('геопозиция.геопозиция')}</h1>
      <div className={st.Map}>
        <GeoLocation center={position} mapProps={{ mouseEvents: false, touchEvents: false }}>
          <GeoLocationMarker anchor={position} />
        </GeoLocation>
      </div>
      <div className={st.InputsContainer}>
        <div className={st.AddressInputs}>
          <div className={st.LongitudeInput}>
            <Control.Input
              errors={errors}
              id="longitude"
              label={t('долгота в градусах')}
              {...register('geo_position.longitude', {
                min: validate.gte(-180),
                max: validate.lte(180),
                validate: {
                  number: validate.number(),
                  validateLongitude: (value) => {
                    if (!value && latitude) {
                      return validate.required();
                    }
                    return true;
                  },
                },
              })}
            />
          </div>
          <div className={st.LatitudeInput}>
            <Control.Input
              errors={errors}
              id="latitude"
              label={t('широта в градусах')}
              {...register('geo_position.latitude', {
                min: validate.gte(-90),
                max: validate.lte(90),
                validate: {
                  number: validate.number(),
                  validateLatitude: (value) => {
                    if (!value && longitude) {
                      return validate.required();
                    }
                    return true;
                  },
                },
              })}
            />
          </div>
        </div>
        <div className={st.AddressInputs}>
          <div className={st.CityInput}>
            <Control.Input
              id="city"
              label={t('геопозиция.город')}
              {...register('city', {
                maxLength: validate.maxLength(36),
              })}
              errors={errors}
            />
          </div>
          <div className={st.RegionInput}>
            <Control.Input
              id="area"
              label={t('геопозиция.область')}
              {...register('area', {
                maxLength: validate.maxLength(36),
              })}
              errors={errors}
            />
          </div>
        </div>
        <div className={st.AddressInputs}>
          <div className={st.DistrictInput}>
            <Control.Input
              id="district"
              label={t('геопозиция.район')}
              {...register('district', {
                maxLength: validate.maxLength(36),
              })}
              errors={errors}
            />
          </div>
          <div className={st.StreetHouseInputs}>
            <div className={st.StreetInput}>
              <Control.Input
                id="street"
                label={t('геопозиция.улица')}
                {...register('street', {
                  maxLength: validate.maxLength(36),
                })}
                errors={errors}
              />
            </div>
            <div className={st.HouseInput}>
              <Control.Input
                id="house_number"
                label={t('геопозиция.дом')}
                {...register('house_number', {
                  maxLength: validate.maxLength(36),
                })}
                errors={errors}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={st.Buttons}>
        <div className={st.ButtonWrapper}>
          <Control.Button
            fullWidth
            kind="primary"
            onClick={onSubmit}
            type="button"
            variant="filled"
          >
            {t('кнопка.сохранить')}
          </Control.Button>
        </div>
        <div className={st.ButtonWrapper}>
          <Control.Button
            fullWidth
            kind="secondary"
            onClick={closeModal}
            type="button"
            variant="outlined"
          >
            {t('кнопка.отмена')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}

GeopositionSubForm.propTypes = {
  fieldName: PropTypes.string,
  closeModal: PropTypes.func,
};
